import React from "react";
import { memberDisplayName } from "../../utils/memberDisplay";
import styles from "./LeadAssigneeSelect.module.css";

/**
 * LeadAssigneeSelect — escolhe o responsável por um lead.
 *
 * `<select>` nativo (acessível e navegável por teclado por padrão, sem lib nova),
 * no mesmo espírito do `MessageCenter/AssigneeSelect` — mas **sem reusar** aquele
 * componente, que é acoplado a sessão de chat, setores e papel do usuário.
 *
 * ⚠️ Contrato da API (verificado em `backend/src/controllers/leadController.ts`):
 * `PATCH /api/leads/:id` valida o corpo com `z.strictObject({ assignedTo:
 * z.string().uuid().optional(), … })`. Ou seja, a API aceita **um uuid ou a
 * omissão do campo** — `null` é rejeitado com 400 (`invalid_type`), e omitir é
 * um no-op. Não existe, hoje, forma de **remover** o responsável de um lead.
 * Por isso a opção "sem responsável" existe apenas como estado inicial e fica
 * desabilitada assim que há alguém atribuído (em vez de disparar um request que
 * o backend recusaria).
 *
 * Props:
 *   value    {string|null}          — `lead.assignedTo` (id do **User**)
 *   members  {Array}                — `TenantMember[]` de `useTenantMembers`
 *   loading  {boolean}              — equipe ainda carregando
 *   error    {boolean}              — falha ao carregar a equipe
 *   busy     {boolean}              — PATCH de atribuição em andamento
 *   onChange {(userId) => void}     — chamado com o `userId` escolhido
 *   id       {string}               — id do `<select>` (para htmlFor externo)
 */
export default function LeadAssigneeSelect({
  value = null,
  members = [],
  loading = false,
  error = false,
  busy = false,
  onChange,
  id = "lead-assignee-select",
}) {
  const hasAssignee = Boolean(value);
  const isKnown = members.some((m) => m?.userId === value);

  const handleChange = (e) => {
    const next = e.target.value;
    // "" = placeholder (desabilitado quando já há responsável) e reescolher a
    // mesma pessoa não gera request.
    if (!next || next === value) return;
    onChange?.(next);
  };

  if (error) {
    return (
      <p className={styles.error} role="alert">
        Não conseguimos carregar sua equipe agora, então não dá para escolher um
        responsável. O resto do lead continua disponível.
      </p>
    );
  }

  return (
    <div className={styles.wrap}>
      <select
        id={id}
        className={styles.select}
        value={hasAssignee ? value : ""}
        onChange={handleChange}
        disabled={busy || loading}
        aria-busy={busy || loading}
        aria-label="Responsável pelo lead"
      >
        <option value="" disabled={hasAssignee}>
          {loading
            ? "Carregando equipe…"
            : hasAssignee
              ? "Sem responsável (indisponível)"
              : "Sem responsável"}
        </option>

        {/* Responsável gravado que não está mais na equipe (membro removido):
            mantemos uma opção com o valor atual para o select não ficar vazio. */}
        {hasAssignee && !isKnown && !loading && (
          <option value={value}>Responsável não identificado</option>
        )}

        {members.map((m) => (
          <option key={m.userId} value={m.userId}>
            {memberDisplayName(m)}
          </option>
        ))}
      </select>

      {hasAssignee && !loading && (
        <p className={styles.hint}>
          Para trocar, escolha outra pessoa da equipe — por enquanto não dá para
          deixar o lead sem responsável.
        </p>
      )}

      {!hasAssignee && !loading && members.length === 0 && (
        <p className={styles.hint}>
          Você ainda não tem outras pessoas na equipe para atribuir.
        </p>
      )}
    </div>
  );
}
